import React, { useState } from 'react';
import { AlertCircle, Loader2, Sparkles } from 'lucide-react';
import { FormSection } from '../playbooks/editorBits';
import BatchRunProgress, { previewLine } from '../../structured-data/BatchRunProgress';
import useBatchRun from '../../structured-data/useBatchRun';

// "Run against all matching contracts (n)" — batch-run the SAVED schema over
// everything its targeting matches. Drafts don't have this (use Test run):
// the batch runs what's saved, so unsaved edits would silently not apply.
//
// The matching-count preview is OWNED BY THE EDITOR (doc-105): the sticky
// header's "N matching" fact and this pane's button share one request per
// saved schema. `onRefreshPreview` drops it (e.g. after a settled batch moved
// the counts) so the editor refetches.
export default function BatchRunPane({ schema, isDirty, preview = null, previewError = null, onRefreshPreview }) {
  const [confirming, setConfirming] = useState(false);
  const batchRun = useBatchRun({
    // Counts moved (fresh outputs now exist) — refresh the button's number.
    onSettled: () => onRefreshPreview?.(),
  });

  const schemaId = schema?.id || null;
  const active = !!schema?.active;

  if (!schemaId) return null;

  const run = async () => {
    setConfirming(false);
    await batchRun.start(schemaId, { matching: true });
  };

  return (
    <FormSection
      title="Run against matching contracts"
      hint="Batch-runs the last SAVED version of this schema over every contract it targets. Contracts with a current output are served from cache and skipped."
    >
      {preview?.coverage && (
        <div className="stack-sm" aria-live="polite">
          <p style={{ fontSize: 13, margin: 0 }}>
            {preview.coverage.complete} completed · {preview.coverage.pending} pending · {preview.coverage.failed} failed · {preview.coverage.missing} not run
            {preview.coverage.stale > 0 ? ` · ${preview.coverage.stale} completed results need updating` : ''}
          </p>
          <p className="muted" style={{ fontSize: 12, margin: 0 }}>
            Latest result update: {preview.lastOutputAt ? new Date(preview.lastOutputAt).toLocaleString() : 'No results yet'}.
            {' '}New results need verification before appearing in verified-only API queries.
          </p>
          <button type="button" className="btn ghost sm" style={{ alignSelf: 'flex-start' }} onClick={onRefreshPreview}>Refresh counts</button>
        </div>
      )}
      {!active && (
        <div className="muted" style={{ fontSize: 12.5 }}>
          This schema is inactive — reactivate it to run batches.
        </div>
      )}

      {active && (batchRun.batch !== null || batchRun.starting) && (
        <div className="stack-sm">
          <BatchRunProgress batch={batchRun.batch} />
          {batchRun.settled && (
            <div>
              <button type="button" className="btn ghost sm" onClick={() => batchRun.reset()}>
                Done
              </button>
            </div>
          )}
        </div>
      )}

      {active && batchRun.batch === null && !batchRun.starting && (
        <div className="stack-sm">
          {previewError && (
            <div className="callout red">
              <div className="callout-icon"><AlertCircle className="w-4 h-4" /></div>
              <div className="callout-body" style={{ fontSize: 12.5 }}>Couldn't check matching contracts: {previewError}</div>
            </div>
          )}
          {batchRun.error && (
            <div className="callout red">
              <div className="callout-icon"><AlertCircle className="w-4 h-4" /></div>
              <div className="callout-body" style={{ fontSize: 12.5 }}>Couldn't start the run: {batchRun.error}</div>
            </div>
          )}
          {!confirming ? (
            <div className="row" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <button
                type="button"
                className="btn outline sm"
                onClick={() => setConfirming(true)}
                disabled={!preview || preview.matching === 0}
              >
                {preview || previewError ? <Sparkles className="w-3.5 h-3.5" /> : <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Run against all matching contracts{preview ? ` (${preview.matching})` : ''}
              </button>
              {preview && preview.matching === 0 && (
                <span className="muted" style={{ fontSize: 12 }}>No contracts match this schema's targeting.</span>
              )}
              {isDirty && (
                <span className="muted" style={{ fontSize: 12 }}>You have unsaved edits — the batch runs the saved version.</span>
              )}
            </div>
          ) : (
            <div className="row" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: 13 }}>{previewLine(preview, { noun: 'matching' })}</span>
              <button type="button" className="btn primary sm" onClick={run} disabled={!preview || preview.willRun === 0}>
                <Sparkles className="w-3.5 h-3.5" />
                Run {preview ? preview.willRun : ''} now
              </button>
              <button type="button" className="btn ghost sm" onClick={() => setConfirming(false)}>
                Cancel
              </button>
            </div>
          )}
        </div>
      )}
    </FormSection>
  );
}
